import { useTabParams } from "@/contexts/TabParamsContext";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useTabLabel } from "@/hooks/useTabLabel";
import {
  usePlatformSummary,
  useCompanyUsersForPlatform,
  useCompanyProjectsForPlatform,
  type PlatformCompanySummary,
  type CompanyUser,
} from "@/hooks/usePlatformSummary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Building2, Users, Briefcase, Settings, Send, CreditCard, Pencil, Loader2, Bot, AlertTriangle,
  CheckCircle2, Clock, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import EditSubscriptionDialog from "@/components/platform/EditSubscriptionDialog";
import { SEO } from "@/components/SEO";
import { CopilotDrawer } from "@/components/platform/copilot/CopilotDrawer";
import { IncidentInbox } from "@/components/platform/incidents/IncidentInbox";
import type { CopilotTenantContext } from "@/hooks/useCopilot";
import type { TenantIncident } from "@/hooks/useTenantIncidents";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

interface CompanyRole {
  id: string;
  name: string;
}

interface OnboardingStep {
  id: string;
  step_key: string;
  step_label: string;
  step_order: number;
  completed: boolean;
  completed_at: string | null;
}

const statusBadgeConfig: Record<string, { variant: "default" | "outline" | "secondary" | "destructive"; label: string }> = {
  active: { variant: "default", label: "Active" },
  invited: { variant: "outline", label: "Invited" },
  pending: { variant: "outline", label: "Pending" },
  suspended: { variant: "destructive", label: "Suspended" },
  paused: { variant: "secondary", label: "Paused" },
};

const TenantDetail = () => {
  const { companyId } = useTabParams<{ companyId: string }>();
  const navigate = useTabNavigate();
  const { data: companies = [], isLoading: loadingSummary } = usePlatformSummary();
  const company = companies.find((c) => c.company_id === companyId) ?? null;
  useTabLabel(company?.company_name ? company.company_name : undefined);

  const { data: users = [], isLoading: loadingUsers, refetch: refetchUsers } =
    useCompanyUsersForPlatform(companyId ?? null);
  const { data: projects = [], isLoading: loadingProjects } =
    useCompanyProjectsForPlatform(companyId ?? null);

  // Fetch roles for the company
  const { data: companyRoles = [] } = useQuery({
    queryKey: ["platform-company-roles", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles")
        .select("id, name")
        .eq("company_id", companyId!);
      if (error) throw error;
      return (data ?? []) as CompanyRole[];
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });

  // Fetch onboarding steps
  const { data: onboardingSteps = [], isLoading: loadingOnboarding } = useQuery({
    queryKey: ["platform-onboarding-steps", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_onboarding_steps" as any)
        .select("*")
        .eq("tenant_id", companyId!)
        .order("step_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as OnboardingStep[];
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });

  const [editCompany, setEditCompany] = useState<PlatformCompanySummary | null>(null);
  const [reinviting, setReinviting] = useState<string | null>(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [showIncidents, setShowIncidents] = useState(false);

  // Edit user state
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", role_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingUser) {
      const matchedRole = companyRoles.find((r) => r.name === editingUser.role_name);
      setEditForm({
        first_name: editingUser.first_name ?? "",
        last_name: editingUser.last_name ?? "",
        email: editingUser.email ?? "",
        role_id: matchedRole?.id ?? "",
      });
    }
  }, [editingUser, companyRoles]);

  const adminUser = users.find((u) => u.role_name === "super_admin");

  const completedSteps = onboardingSteps.filter((s) => s.completed).length;
  const totalSteps = onboardingSteps.length;
  const onboardingPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const handleReinvite = async (email: string) => {
    setReinviting(email);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke("admin-confirm-user", {
        body: { email, send_recovery: true },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      // Check both res.error and res.data.error
      const error = res.error || res.data?.error;
      if (error) {
        const errMsg = typeof error === "string" ? error : error.message || "Reinvite failed";
        throw new Error(errMsg);
      }

      const emailSent = res.data?.email_sent;
      if (emailSent === false) {
        toast.warning(`Email confirmation updated for ${email}, but the setup email could not be sent. The user can use "Forgot Password" to set up their account.`);
      } else {
        toast.success(`Setup email sent to ${email}`);
      }
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to reinvite user");
    } finally {
      setReinviting(null);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser || !companyId) return;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const body: Record<string, string> = {
        user_id: editingUser.user_id,
        company_id: companyId,
      };
      if (editForm.first_name !== (editingUser.first_name ?? "")) body.first_name = editForm.first_name;
      if (editForm.last_name !== (editingUser.last_name ?? "")) body.last_name = editForm.last_name;
      if (editForm.email !== (editingUser.email ?? "")) body.email = editForm.email;
      if (editForm.role_id) {
        const matchedRole = companyRoles.find((r) => r.name === editingUser.role_name);
        if (editForm.role_id !== matchedRole?.id) body.role_id = editForm.role_id;
      }

      const res = await supabase.functions.invoke("update-tenant-user", {
        body,
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });
      if (res.error) throw new Error(res.error.message ?? "Update failed");
      const result = res.data as { message?: string };
      toast.success(result?.message ?? "User updated");
      setEditingUser(null);
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  if (loadingSummary) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Button variant="ghost" onClick={() => navigate("/platform")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card className="mt-4">
          <CardContent className="p-6 text-muted-foreground">Company not found.</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={`${company.company_name} — Tenant Detail`} description="Tenant management" />

      {/* Header */}
      <header className="border-b bg-card px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/platform")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Building2 className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">{company.company_name}</h1>
          <Badge className="capitalize">{company.subscription_status.replace("_", " ")}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditCompany(company)}>
            <Settings className="h-3.5 w-3.5 mr-1" /> Manage Subscription
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCopilotOpen(true)}>
            <Bot className="h-3.5 w-3.5 mr-1" /> Open Copilot
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowIncidents(!showIncidents)}>
            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> {showIncidents ? "Hide" : "View"} Incidents
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* Subscription Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" /> Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Plan</span>
                <p className="font-semibold capitalize">{company.plan_tier}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <p className="font-semibold capitalize">{company.subscription_status.replace("_", " ")}</p>
              </div>
              <div>
                <span className="text-muted-foreground">MRR</span>
                <p className="font-semibold">{fmt(company.calculated_mrr)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Base Rate</span>
                <p className="font-semibold">{fmt(company.monthly_rate)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Seats</span>
                <p className="font-semibold">{company.paid_seat_count} / {company.included_seats}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Contractors</span>
                <p className="font-semibold">{company.contractor_count} / {company.included_contractors}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Progress */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" /> Onboarding Progress
              {totalSteps > 0 && (
                <Badge variant="secondary" className="text-xs ml-auto">
                  {completedSteps} / {totalSteps}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingOnboarding ? (
              <Skeleton className="h-16 w-full" />
            ) : totalSteps === 0 ? (
              <p className="text-sm text-muted-foreground">No onboarding steps configured.</p>
            ) : (
              <div className="space-y-4">
                <Progress value={onboardingPercent} className="h-2" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {onboardingSteps.map((step) => (
                    <div
                      key={step.step_key}
                      className={`flex items-center gap-2 p-2 rounded-md text-sm ${
                        step.completed
                          ? "bg-primary/5 text-foreground"
                          : "bg-muted/50 text-muted-foreground"
                      }`}
                    >
                      {step.completed ? (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{step.step_label}</p>
                        {step.completed_at && (
                          <p className="text-[10px] text-muted-foreground">
                            {new Date(step.completed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Contact */}
        {adminUser && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Admin Contact</p>
              <p className="text-sm font-medium">
                {[adminUser.first_name, adminUser.last_name].filter(Boolean).join(" ") || "—"}
              </p>
              <p className="text-sm text-muted-foreground">{adminUser.email || "—"}</p>
              {adminUser.invited_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Invited: {new Date(adminUser.invited_at).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Users */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" /> Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingUsers ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : users.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No users found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const isInvited = u.status === "invited" || u.status === "pending";
                    const badge = statusBadgeConfig[u.status ?? "active"] ?? { variant: "secondary" as const, label: u.status ?? "unknown" };
                    return (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{u.email || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize text-xs">
                            {u.role_name?.replace("_", " ") || "no role"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={badge.variant} className="capitalize text-xs">
                            {badge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {u.invited_at
                            ? new Date(u.invited_at).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {u.last_login
                            ? new Date(u.last_login).toLocaleDateString()
                            : "Never"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              title="Edit user"
                              onClick={() => setEditingUser(u)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {isInvited && u.email && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={reinviting === u.email}
                                onClick={() => handleReinvite(u.email!)}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                {reinviting === u.email ? "Sending…" : "Resend Invite"}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Projects */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" /> Projects ({projects.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingProjects ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : projects.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No projects found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.project_number ? `#${p.project_number}` : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize text-xs">{p.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Incidents Section */}
        {showIncidents && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-primary" /> Incidents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <IncidentInbox
                companyId={companyId}
                maxItems={10}
                onInvestigate={(incident) => {
                  setCopilotOpen(true);
                }}
              />
            </CardContent>
          </Card>
        )}
      </main>

      <EditSubscriptionDialog
        company={editCompany}
        open={!!editCompany}
        onOpenChange={(o) => { if (!o) setEditCompany(null); }}
      />

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(o) => { if (!o) setEditingUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update profile for {editingUser?.email ?? "this user"} in {company?.company_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">First Name</Label>
                <Input
                  id="edit-first-name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Last Name</Label>
                <Input
                  id="edit-last-name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role_id} onValueChange={(v) => setEditForm((f) => ({ ...f, role_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {companyRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id} className="capitalize">
                      {r.name.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={handleSaveUser} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CopilotDrawer
        open={copilotOpen}
        onOpenChange={setCopilotOpen}
        initialTenantContext={
          companyId ? { companyId, companyName: company?.company_name ?? "Unknown" } : null
        }
      />
    </div>
  );
};

export default TenantDetail;
